import { GetStaticProps } from 'next';
import React, { useEffect, useMemo, useState } from 'react';

import {
  DataCMSTypes,
  JoinUsPageType,
  ModuleListProps,
  OpportunitiesData,
} from 'containers/JoinUsPage/JoinUsPage.types';
import FaqComponent from 'containers/JoinUsPage/sections/FaqComponent/FaqComponent';
import JoinUsGallerySection from 'containers/JoinUsPage/sections/JoinUsGallerySection/JoinUsGallerySection';
import JoinUsImageBlock from 'containers/JoinUsPage/sections/JoinUsImageBlock/JoinUsImageBlock';
import JoinUsInternship from 'containers/JoinUsPage/sections/JoinUsInternship/JoinUsInternship';
import JoinUsOpportunities from 'containers/JoinUsPage/sections/JoinUsOpportunities/JoinUsOpportunities';
import { opportunitiesJobs } from 'containers/JoinUsPage/sections/JoinUsOpportunities/JoinUsOpportunities.utils';
import OpportunityOverlayWrapper from 'containers/JoinUsPage/sections/OpportunityOverlayWrapper/OpportunityOverlayWrapper';
import SectionComponent from 'containers/JoinUsPage/sections/SectionComponent/SectionComponent';
import SectionsHero from 'containers/JoinUsPage/sections/SectionsHero/SectionsHero';
import ValuesSection from 'containers/JoinUsPage/sections/ValuesSection/ValuesSection';
import { useCopyStore, useGlobalStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { queryCMS } from 'utils/cms/api';
import { joinPage } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { ROUTES } from 'utils/routes';
import { marketPageId, MarketPages } from 'utils/sharedStrapiQueries/market';
import { pageMotionProps } from 'utils/styles/animations';

import JoinUsHero from './sections/JoinUsHero/JoinUsHero';

import * as S from './JoinUsPage.styles';

export interface JoinUsPageProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    global: CopyStoreType['copy']['global'];
    globalSettings: CopyStoreType['copy']['globalSettings'];
    redirectPopup: CopyStoreType['copy']['redirectPopup'];
    cookieBar: CopyStoreType['copy']['cookieBar'];
    navbar: CopyStoreType['copy']['navbar'];
    footer: CopyStoreType['copy']['footer'];
  };
  data: JoinUsPageType;
}

const defaultProps: Partial<JoinUsPageProps> = {};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const id = await marketPageId(MarketPages.joinUsPage, locale);
  const cookieId = await marketPageId(MarketPages.cookieBar, locale);
  const footerId = await marketPageId(MarketPages.footer, locale);

  const globalIds = { cookieId, footerId };

  const response = await queryCMS<DataCMSTypes>({
    query: joinPage({
      id,
      locale,
      globalIds,
    }),
  });

  const {
    data: { sharedCopies, redirectPopups, cookieBar, navbar, footer },
  } = response;

  const data: JoinUsPageType = response.data.joinUsPage;

  if (!data || !sharedCopies) return { notFound: true };

  return {
    props: {
      initialCopy: {
        head: data?.seo || null,
        global: sharedCopies[0]?.global || null,
        globalSettings: sharedCopies[0] || null,
        redirectPopup: redirectPopups[0] || null,
        cookieBar: cookieBar || null,
        navbar,
        footer,
      },
      data,
    },
    revalidate: ISR_TIMEOUT,
  };
};

const JoinUsPage: React.FunctionComponent<JoinUsPageProps> = ({
  initialCopy,
  data,
}) => {
  const { setCopy } = useCopyStore();
  const { setHeaderText } = useGlobalStore();
  const [opportunitiesData, setOpportunitiesData] = useState<OpportunitiesData>(
    {
      opportunities: [],
      departments: [],
      locations: [],
    }
  );
  const faqData = useMemo(
    () =>
      data.modules
        .filter(module => module.__typename === 'ComponentSectionsFaqSection')
        // @ts-ignore
        .map(faqModule => faqModule.faq)
        .flat(),
    [data]
  );

  useEffect(() => {
    setCopy(initialCopy);
    setHeaderText({
      default: initialCopy?.navbar?.navLinks.find(
        ({ href }) => href === ROUTES.JOIN_US
      )?.headerText,
    });

    (async () => {
      const data = await opportunitiesJobs();
      setOpportunitiesData(data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  const isFirstHero = data.modules[0]?.__typename.includes(
    'ComponentSectionsJoinUsHero'
  );

  const modulesList: ModuleListProps = {
    ComponentSectionsHeadlineSection: S.IndexPageHeadline,
    ComponentSectionsHero: SectionsHero,
    ComponentSectionsJoinUsHero: JoinUsHero,
    ComponentSubComponentsSection: SectionComponent,
    ComponentSubComponentsSectionTitle: S.ParallaxTitle,
    ComponentSectionsValuesSection: ValuesSection,
    ComponentSectionsEventsGallerySection: JoinUsGallerySection,
    ComponentSectionsInternship: JoinUsInternship,
    ComponentSubComponentsImageBlock: JoinUsImageBlock,
    ComponentSectionsJobList: JoinUsOpportunities,
    ComponentSectionsFaqSection: FaqComponent,
  };

  const renderModules = data.modules.map((moduleData, idx) => {
    const moduleName: string = moduleData.__typename;
    const ModuleComponent = modulesList[moduleName];

    if (!ModuleComponent) return null;

    return (
      <ModuleComponent
        key={`${moduleName}${idx}`}
        opportunitiesData={opportunitiesData}
        {...moduleData}
      />
    );
  });

  return (
    <S.JoinUsPageWrapper isFirstHero={isFirstHero} {...pageMotionProps}>
      {renderModules}
      <OpportunityOverlayWrapper
        faqData={faqData}
        opportunitiesData={opportunitiesData}
        pageData={data}
      />
    </S.JoinUsPageWrapper>
  );
};

JoinUsPage.defaultProps = defaultProps;

export default JoinUsPage;
